import { useTenant } from '@plato/app-core/hooks/use-tenant';
import { useUser } from '@plato/app-core/hooks/use-user';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@plato/ui/primitives/tooltip';
import { UserXIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useImpersonation } from '../../../lib/hooks/use-impersonation.ts';
import { getFullName } from '../../../utils/name.ts';

type ImpersonationBannerProps = {
    expanded?: boolean;
};

export function ImpersonationBanner({ expanded = true }: ImpersonationBannerProps) {
    const { t } = useTranslation('common');
    const [impersonatedUserId, setImpersonatedUserId] = useImpersonation();
    const user = useUser();
    const tenant = useTenant();
    const navigate = useNavigate();

    if (!impersonatedUserId) {
        return null;
    }

    const exitImpersonation = () => {
        setImpersonatedUserId(undefined);
        void navigate(`/admin/customers/${encodeURIComponent(tenant.id)}`);
    };

    const fullName = getFullName(user.firstName, user.lastName);
    const customerName = tenant.name;
    const buttonLabel = `${customerName}${fullName ? ` (${fullName})` : ''}`;

    if (expanded) {
        return (
            <Tooltip>
                <TooltipTrigger
                    render={
                        <Button
                            variant="ghost"
                            onClick={exitImpersonation}
                            leftIcon={UserXIcon}
                            className="w-full min-w-0 justify-start [&>svg]:shrink-0"
                            aria-label={t('exit_demo__button')}
                        />
                    }
                >
                    <span className="truncate font-normal">{buttonLabel}</span>
                </TooltipTrigger>
                <TooltipContent side="right">{t('exit_demo__button')}</TooltipContent>
            </Tooltip>
        );
    }

    return (
        <Tooltip>
            <TooltipTrigger
                render={
                    <Button
                        variant="ghost"
                        onClick={exitImpersonation}
                        icon={UserXIcon}
                        aria-label={t('exit_demo__button')}
                    />
                }
            />
            <TooltipContent side="right">{buttonLabel}</TooltipContent>
        </Tooltip>
    );
}
